var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import cx from 'classnames';
import React from 'react';
import { useTheme, useMergedRefs, getBoundedValue, getWindow, useContainerWidth, } from '../utils';
import '@itwin/itwinui-css/css/tabs.css';
import { Tab } from './Tab';
/**
 * Tabs organize and allow navigation between groups of content that are related and at the same level of hierarchy.
 * @example
 * const tabs = [
 *   <Tab label='Label 1' />,
 *   <Tab label='Label 2' />,
 *   <Tab label='Label 3' />,
 * ];
 * <Tabs labels={tabs} />
 *
 * @example
 * const tabsWithSublabels = [
 *   <Tab label='Label 1' sublabel='First tab' />,
 *   <Tab label='Label 2' sublabel='Active tab' />,
 * ];
 * <Tabs labels={tabsWithSublabels} activeIndex={1} />
 *
 * @example
 * const tabsWithIcons = [
 *   <Tab label='Label 1' icon={<SvgPlaceholder />} />,
 *   <Tab label='Label 2' icon={<SvgPlaceholder />} />,
 * ];
 * <Tabs labels={tabsWithIcons} type='pill' />
 */
export var Tabs = function (props) {
    var _a, _b, _c;
    var labels = props.labels, activeIndex = props.activeIndex, onTabSelected = props.onTabSelected, _d = props.focusActivationMode, focusActivationMode = _d === void 0 ? 'auto' : _d, _e = props.type, type = _e === void 0 ? 'default' : _e, _f = props.color, color = _f === void 0 ? 'blue' : _f, _g = props.orientation, orientation = _g === void 0 ? 'horizontal' : _g, tabsClassName = props.tabsClassName, contentClassName = props.contentClassName, wrapperClassName = props.wrapperClassName, children = props.children, rest = __rest(props, ["labels", "activeIndex", "onTabSelected", "focusActivationMode", "type", "color", "orientation", "tabsClassName", "contentClassName", "wrapperClassName", "children"]);
    useTheme();
    var tablistRef = React.useRef(null);
    var _h = useContainerWidth(type !== 'default'), tablistSizeRef = _h[0], tabsWidth = _h[1];
    var refs = useMergedRefs(tablistRef, tablistSizeRef);
    var _j = React.useState(function () {
        return activeIndex != null
            ? getBoundedValue(activeIndex, 0, labels.length - 1)
            : 0;
    }), currentActiveIndex = _j[0], setCurrentActiveIndex = _j[1];
    React.useLayoutEffect(function () {
        if (activeIndex != null && currentActiveIndex !== activeIndex) {
            setCurrentActiveIndex(getBoundedValue(activeIndex, 0, labels.length - 1));
        }
    }, [activeIndex, currentActiveIndex, labels.length]);
    // CSS custom properties to place the active stripe
    var _k = React.useState({}), stripeProperties = _k[0], setStripeProperties = _k[1];
    React.useLayoutEffect(function () {
        if (type !== 'default' && tablistRef.current != undefined) {
            var activeTab = tablistRef.current.children[currentActiveIndex];
            var activeTabRect = activeTab.getBoundingClientRect();
            setStripeProperties(__assign(__assign({}, (orientation === 'horizontal' && {
                '--stripe-width': "".concat(activeTabRect.width, "px"),
                '--stripe-left': "".concat(activeTab.offsetLeft, "px"),
            })), (orientation === 'vertical' && {
                '--stripe-height': "".concat(activeTabRect.height, "px"),
                '--stripe-top': "".concat(activeTab.offsetTop, "px"),
            })));
        }
    }, [currentActiveIndex, type, orientation, tabsWidth]);
    var _l = React.useState(), focusedIndex = _l[0], setFocusedIndex = _l[1];
    React.useEffect(function () {
        if (tablistRef.current && focusedIndex !== undefined) {
            var tab = tablistRef.current.querySelectorAll('.iui-tab')[focusedIndex];
            tab === null || tab === void 0 ? void 0 : tab.focus();
        }
    }, [focusedIndex]);
    var _m = React.useState(false), hasSublabel = _m[0], setHasSublabel = _m[1]; // used for setting size
    React.useLayoutEffect(function () {
        var _a;
        setHasSublabel(type !== 'pill' && // pill tabs should never have sublabels
            !!((_a = tablistRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.iui-tab-description')));
    }, [type]);
    var onTabClick = function (index) {
        if (onTabSelected) {
            onTabSelected(index);
        }
        setCurrentActiveIndex(index);
    };
    var onKeyDown = function (event) {
        // alt + arrow keys are used by browser / assistive technologies
        if (event.altKey) {
            return;
        }
        var isTabDisabled = function (index) {
            var tab = labels[index];
            return React.isValidElement(tab) && tab.props.disabled;
        };
        var newIndex = focusedIndex !== null && focusedIndex !== void 0 ? focusedIndex : currentActiveIndex;
        /** focus next tab if delta is +1, previous tab if -1 */
        var focusTab = function (delta) {
            if (delta === void 0) { delta = +1; }
            do {
                newIndex = (newIndex + delta + labels.length) % labels.length;
            } while (isTabDisabled(newIndex) && newIndex !== focusedIndex);
            setFocusedIndex(newIndex);
            focusActivationMode === 'auto' && onTabClick(newIndex);
        };
        switch (event.key) {
            case 'ArrowDown': {
                if (orientation === 'vertical') {
                    focusTab(+1);
                    event.preventDefault();
                }
                break;
            }
            case 'ArrowRight': {
                if (orientation === 'horizontal') {
                    focusTab(+1);
                    event.preventDefault();
                }
                break;
            }
            case 'ArrowUp': {
                if (orientation === 'vertical') {
                    focusTab(-1);
                    event.preventDefault();
                }
                break;
            }
            case 'ArrowLeft': {
                if (orientation === 'horizontal') {
                    focusTab(-1);
                    event.preventDefault();
                }
                break;
            }
            case 'Enter':
            case ' ':
            case 'Spacebar': {
                event.preventDefault();
                if (focusActivationMode === 'manual' && focusedIndex !== undefined) {
                    onTabClick(focusedIndex);
                }
                break;
            }
            default:
                break;
        }
    };
    var isIE = !((_c = (_b = (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.CSS) === null || _b === void 0 ? void 0 : _b.supports) === null || _c === void 0 ? void 0 : _c.call(_b, '--stripe-width', '100px'));
    return (React.createElement("div", { className: cx('iui-tabs-wrapper', "iui-".concat(orientation), wrapperClassName), style: stripeProperties },
        React.createElement("ul", __assign({ className: cx('iui-tabs', "iui-".concat(type), {
                'iui-green': color === 'green',
                'iui-animated': type !== 'default' && !isIE,
                'iui-not-animated': isIE,
                'iui-large': hasSublabel,
            }, tabsClassName), role: 'tablist', ref: refs, onKeyDown: onKeyDown }, rest), labels.map(function (label, index) {
            var onClick = function () {
                setFocusedIndex(index);
                onTabClick(index);
            };
            return (React.createElement("li", { key: index }, !React.isValidElement(label) ? (React.createElement(Tab, { label: label, className: cx({
                    'iui-active': index === currentActiveIndex,
                }), tabIndex: index === currentActiveIndex ? 0 : -1, onClick: onClick, "aria-selected": index === currentActiveIndex })) : (React.cloneElement(label, {
                className: cx(label.props.className, {
                    'iui-active': index === currentActiveIndex,
                }),
                'aria-selected': index === currentActiveIndex,
                tabIndex: index === currentActiveIndex ? 0 : -1,
                onClick: function (args) {
                    var _a, _b;
                    onClick();
                    (_b = (_a = label.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, args);
                },
            }))));
        })),
        children && (React.createElement("div", { className: cx('iui-tabs-content', contentClassName), role: 'tabpanel' }, children))));
};
/**
 * Tabs organize and allow navigation between groups of content that are related and at the same level of hierarchy.
 * @example
 * const tabs = [
 *   <Tab label='Label 1' sublabel='First tab' />,
 *   <Tab label='Label 2' sublabel='Active tab' />,
 *   <Tab label='Label 3' sublabel='Disabled tab' disabled icon={<SvgPlaceholder />} />,
 * ];
 * <HorizontalTabs labels={tabs} activeIndex={1}>Tabpanel content</HorizontalTabs>
 */
export var HorizontalTabs = function (props) { return (React.createElement(Tabs, __assign({ orientation: 'horizontal' }, props))); };
/**
 * Tabs organize and allow navigation between groups of content that are related and at the same level of hierarchy.
 * @example
 * const tabs = [
 *   <Tab label='Label 1' sublabel='First tab' />,
 *   <Tab label='Label 2' sublabel='Active tab' />,
 *   <Tab label='Label 3'  sublabel='Disabled tab' disabled icon={<SvgPlaceholder />} />,
 * ];
 * <VerticalTabs labels={tabs} activeIndex={1}>Tabpanel content</VerticalTabs>
 */
export var VerticalTabs = function (props) { return (React.createElement(Tabs, __assign({ orientation: 'vertical' }, props))); };
export default Tabs;
